import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useDrag } from "@use-gesture/react";
import { useSceneStore } from "@/state/useSceneStore";
import { cameraTargets } from "@/scene/cameras/cameraTargets";
import { ProjectsIndex } from "@/ui/MonitorContent/Projects/Index";
import { ContactForm } from "@/ui/MonitorContent/Contact/Form";

const SHOTS = ["hero", "projects", "contact"] as const;
type Shot = (typeof SHOTS)[number];

interface HeroProps {
  onContact: () => void;
  onProjects: () => void;
}

const Hero = ({ onContact, onProjects }: HeroProps): JSX.Element => (
  <div className="text-center">
    <h1 className="font-display text-3xl">Adam Kotlorz</h1>
    <p className="mt-1 text-sm text-ink1">Software Engineer</p>
    <div className="mt-4 flex justify-center gap-3">
      <button
        type="button"
        onClick={onProjects}
        className="border border-rule px-4 py-2 font-mono text-xs uppercase"
      >
        My work
      </button>
      <button
        type="button"
        onClick={onContact}
        className="border border-phosphor px-4 py-2 font-mono text-xs uppercase text-phosphor"
      >
        Hire me
      </button>
    </div>
  </div>
);

const shotToSection = (s: Shot) => {
  if (s === "hero") return null;
  if (s === "projects") return "projects" as const;
  return "contact" as const;
};

export const MobileCarousel = (): JSX.Element => {
  const [shot, setShot] = useState<Shot>("hero");
  const setSection = useSceneStore((s) => s.setActiveSection);
  const requestCameraMove = useSceneStore((s) => s.requestCameraMove);

  const jump = (next: Shot): void => {
    setShot(next);
    requestCameraMove(cameraTargets.mobileShots[next], 1.0);
    setSection(shotToSection(next));
  };

  useEffect(() => {
    setSection(shotToSection(shot));
  }, [shot, setSection]);

  const bind = useDrag(
    ({ swipe: [sx] }) => {
      if (sx === 0) return;
      const i = SHOTS.indexOf(shot);
      const nextIdx =
        sx > 0 ? Math.max(0, i - 1) : Math.min(SHOTS.length - 1, i + 1);
      const next = SHOTS[nextIdx];
      if (next && next !== shot) jump(next);
    },
    { swipe: { distance: 50 } },
  );

  return (
    <div className="absolute inset-x-0 bottom-0 z-20">
      <div
        data-testid="mobile-panel"
        className="relative mx-auto max-h-[75vh] w-full overflow-y-auto overscroll-contain bg-bg1/95 p-4 backdrop-blur-md font-sans"
      >
        {shot !== "hero" && (
          <button
            type="button"
            onClick={() => jump("hero")}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 font-mono text-base leading-none text-ink2 hover:text-phosphor"
          >
            ×
          </button>
        )}
        {shot === "hero" && (
          <Hero
            onContact={() => jump("contact")}
            onProjects={() => jump("projects")}
          />
        )}
        {shot === "projects" && <ProjectsIndex />}
        {shot === "contact" && <ContactForm />}
      </div>
      <div
        {...bind()}
        data-testid="mobile-swipe-handle"
        aria-hidden="true"
        className="flex touch-pan-x justify-center bg-bg0/95 pt-2"
      >
        <span className="h-1 w-10 rounded-full bg-ink2/60" />
      </div>
      <nav
        {...bind()}
        className="flex touch-pan-x justify-center gap-3 bg-bg0/95 py-2"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {SHOTS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => jump(s)}
            aria-current={shot === s ? "page" : undefined}
            className={`font-mono text-[10px] uppercase ${shot === s ? "text-phosphor" : "text-ink2"}`}
          >
            {s}
          </button>
        ))}
      </nav>
    </div>
  );
};
